import React, { FC, useEffect, useMemo, useState } from 'react'
import {
  Form,
  Forms,
  useFormsContext,
  RadioButton,
  RadioGroup,
} from '@ally/forms'
import {
  Box,
  Button,
  ButtonGroup,
  Label,
  upToMd,
  useModalContext,
  get,
  TextBody,
  useTriggerSnackbar,
  useSteppedProcess,
} from '@ally/metronome-ui'
import styled from 'styled-components'
import dayjs from 'dayjs'
import allytm from '@ally/allytm'
import {
  AddBenePayload,
  AddBenePayloadObject,
  Beneficiary,
  BeneficiaryAddress,
  BeneficiaryIdentifier,
  IdentificationData,
} from '../../../types'
import { useNonIraBeneficiaries } from '../../../services/NonIraBeneficiariesProvider'
import { useHostServices } from '../../../HostServices'
import {
  BENEFICIARY_CLASSIFICATION_CODES,
  GENERIC_ERROR_MESSAGE,
  IDENTIFIER_NAMES,
  MAX_NON_IRA_BENES,
} from '../../../utils/constants'
import { useBeneficiariesApi } from '../../../api/beneficiaries-api'
import { getErrorMessage, removeLeadingZeros } from '../../../utils/formatter'
import {
  StepTwoFormValues,
  stepTwoBeneErrors,
} from '../../common/AddBeneficiary/StepTwoAddNewBene'
import { FundsAllocationFields } from '../../common/FundsAllocationFields'
import { fireErrorTagging, KeyValuePairObj } from '../../../utils/tagging'

export type StepThreeFormValues = Record<string, string | number>

const SSN = 'SocialSecurityNumber'

const StyledButtonGroup = styled(ButtonGroup)`
  margin-top: ${get.space('md')};
  margin-bottom: ${get.space('md')};
`

export const StepThreeForm: FC<{
  existingBenes?: Beneficiary[]
  isLoading: boolean
  setStepThreeFormValues: React.Dispatch<
    React.SetStateAction<StepThreeFormValues>
  >
}> = ({ existingBenes, isLoading, setStepThreeFormValues }) => {
  const {
    formId,
    values,
    setFieldValue,
    handleSubmit,
    setFormError,
    errors,
    isSubmitting,
  } = useFormsContext()
  const { setOpen } = useModalContext()

  const total = useMemo(() => {
    return Object.keys(values)
      .filter(key => key !== 'fundAllocationType')
      .reduce((acc, curr) => {
        if (Number.isNaN(Number(values[curr]))) {
          return acc
        }
        return acc + Number(values[curr])
      }, 0)
  }, [values])

  const onSubmit = (): void => {
    fireErrorTagging(errors as KeyValuePairObj)
    if (
      (values.fundAllocationType === 'specific' && total === 100) ||
      values.fundAllocationType === 'equal'
    ) {
      setFormError('')
      handleSubmit()
    }
  }

  const handleFundAllocationTypeChange = (selected: string): void => {
    setFieldValue('fundAllocationType', selected)
    setStepThreeFormValues(
      // istanbul ignore next // low value
      prevState => ({
        ...prevState,
        fundAllocationType: selected,
      }),
    )
  }

  const handleInputChange = (name: string, value: string): void => {
    setFieldValue(name, value)
    setStepThreeFormValues(
      // istanbul ignore next // low value
      prevState => ({
        ...prevState,
        [name]: value,
      }),
    )
  }

  return (
    <Form id={formId}>
      {existingBenes && existingBenes.length > 0 ? (
        <>
          <Box width={upToMd('88%', '88%')}>
            <Label htmlFor="fundAllocationType">
              How do you want to reallocate your money?
            </Label>
            <RadioGroup
              name="fundAllocationType"
              legendText=""
              isLegendHidden
              onChange={(selected): void =>
                handleFundAllocationTypeChange(selected)
              }
            >
              <RadioButton
                label="Equally"
                value="equal"
                data-testid="equal-allocation"
              />
              <RadioButton
                label="Specific percentage"
                value="specific"
                data-testid="specific-allocation"
              />
            </RadioGroup>
          </Box>
          {values.fundAllocationType === 'specific' && (
            <FundsAllocationFields
              existingBeneficiaries={existingBenes}
              total={total}
              setPercentageValue={handleInputChange}
            />
          )}
        </>
      ) : (
        <Box mt="md" mb="md">
          <Label htmlFor="allocationAmount">Allocation amount</Label>
          <TextBody tag="span" size="sm">
            100%
          </TextBody>
        </Box>
      )}

      <StyledButtonGroup>
        <Button
          allytmln="Submit Add Non IRA Beneficiary"
          text="Continue"
          onClick={onSubmit}
          loading={isLoading}
          data-testid="submit-continue-button"
          disabled={isSubmitting}
        />
        <Button
          allytmln="Cancel Add Beneficiary"
          text="Cancel"
          onClick={
            // istanbul ignore next
            (): void => setOpen(false)
          }
          variant="link"
        />
      </StyledButtonGroup>
    </Form>
  )
}

export const StepThreeAddNewBeneNonIra: FC<{
  existingBenes?: Beneficiary[]
  beneficiaryClassification: string
  stepTwoFormValues: Partial<StepTwoFormValues>
  distributionType?: string
  accountId: string
  setErrorMessage: React.Dispatch<React.SetStateAction<string>>
  setStepTwoFormErrorCode: React.Dispatch<React.SetStateAction<string>>
  stepThreeFormValues: StepThreeFormValues
  setStepThreeFormValues: React.Dispatch<
    React.SetStateAction<StepThreeFormValues>
  >
}> = ({
  existingBenes,
  beneficiaryClassification,
  stepTwoFormValues,
  distributionType,
  accountId,
  setErrorMessage,
  setStepTwoFormErrorCode,
  stepThreeFormValues,
  setStepThreeFormValues,
}) => {
  const { setPrevStepActive } = useSteppedProcess()
  const { createBeneficiary } = useBeneficiariesApi()
  const [isLoading, setIsLoading] = useState(false)
  const { getBeneficiaries } = useNonIraBeneficiaries()
  const { triggerSnackbar } = useTriggerSnackbar()
  const { hostData } = useHostServices()
  // istanbul ignore next optional chaining
  const customerAddress = hostData?.afgCustomer?.data?.data?.addresses.find(
    (address: any) => address.type === 'PRIMARY',
  )

  const getCustomerAddress = (
    values: Partial<StepTwoFormValues>,
  ): BeneficiaryAddress => {
    if (stepTwoFormValues.sameAddress === 'Yes' && customerAddress) {
      // istanbul ignore next optional chaining
      return {
        addressLine1: customerAddress?.line1,
        addressLine2: customerAddress?.line2 || '',
        city: customerAddress?.city as string,
        state: customerAddress?.state as string,
        zipCode: customerAddress?.zipCode as string,
        country: 'US',
      }
    }

    return {
      addressLine1: values.addressLine1 as string,
      addressLine2: values.addressLine2 || '',
      city: values.city as string,
      state: values.state as string,
      zipCode: values.zipCode as string,
      country: 'US',
    }
  }

  const getIdentificationData = (): IdentificationData => {
    const identificationSelection =
      stepTwoFormValues.beneIdentification &&
      Object.keys(IDENTIFIER_NAMES).find(
        key =>
          IDENTIFIER_NAMES[key as BeneficiaryIdentifier] ===
          stepTwoFormValues.beneIdentification,
      )
    if (
      stepTwoFormValues.beneIdentification === SSN ||
      beneficiaryClassification === 'charity' ||
      beneficiaryClassification === 'other' ||
      beneficiaryClassification === 'trust'
    ) {
      return {
        taxId: stepTwoFormValues.identificationNumber,
        taxIdentifier: [],
      }
    }

    return {
      taxIdentifier: [
        {
          type:
            identificationSelection || // istanbul ignore next // low value
            '',
          typeValue: stepTwoFormValues.identificationNumber as string,
          issuer: stepTwoFormValues.issuer as string,
          issuedDate: dayjs(stepTwoFormValues.issueDate)
            .format('MM-DD-YYYY')
            .toString(),
          expiryDate: dayjs(stepTwoFormValues.expirationDate)
            .format('MM-DD-YYYY')
            .toString(),
        },
      ],
    }
  }

  const newBeneficiaryPayload = (
    values: StepThreeFormValues,
  ): AddBenePayloadObject => {
    const commonPayload = {
      beneficiaryClassificationCode:
        BENEFICIARY_CLASSIFICATION_CODES[beneficiaryClassification],
      address: getCustomerAddress(stepTwoFormValues),
      allocationPercentage:
        values.fundAllocationType === 'equal'
          ? 'Equal'
          : removeLeadingZeros(values.newBeneficiaryFundAllocation),
      ...getIdentificationData(),
    }
    if (
      beneficiaryClassification === 'charity' ||
      beneficiaryClassification === 'other'
    ) {
      return {
        beneficiaryName: stepTwoFormValues.beneficiaryName,
        ...commonPayload,
      }
    }
    if (beneficiaryClassification === 'trust') {
      return {
        whollyOwnedTrust: stepTwoFormValues.whollyOwnedTrust === 'Yes',
        beneficiaryName: stepTwoFormValues.beneficiaryName,
        ...commonPayload,
      }
    }
    return {
      firstName: stepTwoFormValues.firstName,
      middleName: stepTwoFormValues.middleName,
      lastName: stepTwoFormValues.lastName,
      dateOfBirth: dayjs(stepTwoFormValues.dateOfBirth)
        .format('YYYY-MM-DD')
        .toString(),
      ...commonPayload,
    }
  }

  const createPayloadObject = (values: StepThreeFormValues): AddBenePayload => {
    // there are two different scenarios, one where beneficiaries have specific allocation percentages
    // in which we have to pass all beneficiaries with bene id and allocation percentage.
    // the other scenario would be equal allocation for which we only need to pass the new bene.
    const newBeneficiary = newBeneficiaryPayload(values)
    if (values.fundAllocationType === 'equal') {
      return {
        beneficiaries: [newBeneficiary],
        distributionType: stepTwoFormValues.ownershipType || distributionType,
      }
    }

    // customized objects for each bene with allocation percentage and id.
    const allBenePayload = existingBenes
      ? existingBenes.map(bene => ({
          beneficiaryId: bene.beneficiaryId,
          allocationPercentage: removeLeadingZeros(
            values[bene.beneficiaryId as keyof StepThreeFormValues],
          ),
        }))
      : // istanbul ignore next // low value
        []
    return {
      beneficiaries: [newBeneficiary, ...allBenePayload],
      distributionType:
        stepTwoFormValues.ownershipType ||
        /* istanbul ignore next */ distributionType,
    }
  }

  const handleSubmit = async (values: StepThreeFormValues): Promise<void> => {
    const payload = createPayloadObject(values)
    try {
      setErrorMessage('')
      setStepTwoFormErrorCode('')
      setIsLoading(true)
      await createBeneficiary(accountId, payload)
      await getBeneficiaries()
      // istanbul ignore next
      triggerSnackbar({
        text: 'You updated your beneficiaries.',
        columnSize: 4,
        snackbarButtonText: 'Close',
        onClose: () =>
          requestAnimationFrame(() => {
            // This if condition bring focus on max bene warning message
            if (existingBenes?.length === MAX_NON_IRA_BENES - 1) {
              document
                .getElementById(`${accountId}-add-bene-success-focus`)
                ?.getElementsByTagName('span')[0]
                .focus()
            } else {
              document
                .getElementById(`${accountId}-add-bene-success-focus`)
                ?.focus()
            }
          }),
      })
      // istanbul ignore next - tagging
      allytm.event('customEvent', {
        name: 'formSuccess',
        type: 'formValidationSuccess',
        attr: {
          formValues: {
            form: 'Submit New Non-IRA Beneficiary',
            values: [],
          },
        },
      })
    } catch (err: any) {
      const errorMessage = getErrorMessage(err)
      allytm.event(
        'customError',
        stepTwoBeneErrors[errorMessage]
          ? stepTwoBeneErrors[errorMessage].errorMessage
          : 'It’s us – we’re having a moment. We couldn’t add your beneficiary. We’ll be quick about it, so try again in a bit.',
        errorMessage,
      )
      if (
        errorMessage === 'USB_BUS_351' ||
        errorMessage === 'USB_BUS_357' ||
        errorMessage === 'USB_BUS_634' ||
        errorMessage === 'USB_BUS_633' ||
        errorMessage === 'USB_BUS_350'
      ) {
        setStepTwoFormErrorCode(errorMessage)
        // istanbul ignore next
        setPrevStepActive()
      } else {
        setErrorMessage(GENERIC_ERROR_MESSAGE)
        allytm.event(
          'customError',
          'We couldn’t process your request. Please contact Customer Service at 1-877-247-2559.',
        )
      }
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    allytm.event(
      'modalView',
      'AOS:Beneficiaries:Bank Checking Savings and CDs:Add Beneficiary:Allocation',
      {
        treatAsPageview: true,
      },
    )
  }, [])

  return (
    <Forms
      formId="beneficiary-classification-form"
      initialValues={stepThreeFormValues}
      onSubmit={handleSubmit}
    >
      <StepThreeForm
        existingBenes={existingBenes}
        isLoading={isLoading}
        setStepThreeFormValues={setStepThreeFormValues}
      />
    </Forms>
  )
}
